// BuilderHeader - 上部ヘッダー（spec.md §5.8.1）
// なまえ / サイズ / AI / テスト / 保存ずみ / 完成ロゴ / ？ヘルプ。
// もどる・ユーザーアバターはアプリ共通 chrome が描画するため本ヘッダーには持たない。
// 必須条件・Blockly 設定は PublishStepsPanel（§5.8.3/§5.8.7）が担う。

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { AssetModelType } from "common/model";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { actions, selectAllMapModel } from "features/builder/slice";
import { AIChatModal } from "./AIChatModal";
import { BuilderTutorialOverlay } from "./BuilderTutorialOverlay";

export const BuilderHeader = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allMapModel = useSelector(selectAllMapModel);
  const size = useSelector((state: RootState) => state.builder.size);
  const mapName = useSelector((state: RootState) => state.builder.name);
  const draftDirty = useSelector((state: RootState) => state.builder.draftDirty);

  const [showAIModal, setShowAIModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  // テスト活性条件（キャラ + ゴール配置）
  const hasCharacter = allMapModel.some((m) => m.type === AssetModelType.CHARACTER);
  const hasGoal = allMapModel.some((m) => m.type === AssetModelType.GOAL);
  const canTest = hasCharacter && hasGoal;

  const handleOpenAI = () => {
    play();
    btnDelay(() => setShowAIModal(true));
  };

  const handleTest = () => {
    if (!canTest) return;
    play();
    btnDelay(() => navigate("preview"));
  };

  // チュートリアルは ？ボタン押下時のみ（アプリ StartupScreen と衝突するため自動表示しない）
  const handleOpenTutorial = () => {
    play();
    btnDelay(() => setShowTutorial(true));
  };

  return (
    <>
      {/* 中央揃えコンテナ（最上部、アプリ共通 もどる/アバターの右に並ぶ） */}
      <div
        className="flex-row-el flex-center absolute left-0 right-0 z-[9000] pointer-events-none w-full"
        style={{ top: 20 * scale }}
      >
        {/* scale ラッパー */}
        <div
          className="flex-row-el flex-center pointer-events-auto"
          style={{ width: 860 * scale, height: 70 * scale }}
        >
          <div
            className="flex-row-el items-center gap-3 origin-top w-[860px] h-[70px]"
            style={{ transform: `scale(${scale})` }}
          >
            {/* 白ピル */}
            <div className="flex-row-el items-center gap-3 flex-1 h-full px-4 rounded-2xl bg-white/95 backdrop-blur-sm border-4 border-white shadow-lg">
              {/* ドラフト編集中ピル（spec.md §5.8.7） */}
              {draftDirty && (
                <div className="flex-row-el flex-center px-3 py-1 rounded-full bg-yellow-200 whitespace-nowrap">
                  <span className="type-sm text-yellow-800">
                    {I18n.t("MSG_BUILDER_DRAFT_EDITING_PILL")}
                  </span>
                </div>
              )}

              {/* なまえ入力 */}
              <input
                type="text"
                value={mapName}
                onChange={(e) => dispatch(actions.setPieceName(e.target.value))}
                placeholder={I18n.t("MSG_BUILDER_MAP_NAME_PLACEHOLDER")}
                maxLength={20}
                className="input-modal type-body text-textcolor-black flex-1 min-w-0 h-11 px-3"
              />

              {/* サイズ */}
              <div className="flex-row-el flex-center h-11 px-4 rounded-xl bg-gray-100 border-2 border-gray-200 whitespace-nowrap">
                <span className="type-sm text-textcolor-black">
                  {size.x}×{size.z}
                </span>
              </div>

              {/* AI でつくる */}
              <button
                onClick={handleOpenAI}
                className="flex-row-el flex-center gap-1 h-11 px-4 rounded-xl bg-emerald-100 hover:bg-emerald-200 border-2 border-emerald-200 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
              >
                <span className="flex-col-el flex-center type-md text-emerald-600">
                  <SmartToyOutlinedIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-emerald-700">{I18n.t("MSG_AI_LABEL")}</span>
              </button>

              {/* テスト */}
              <button
                onClick={handleTest}
                disabled={!canTest}
                className={`flex-row-el flex-center gap-1 h-11 px-4 rounded-xl border-2 transition-colors active:scale-95 type-inherit-font whitespace-nowrap ${
                  canTest
                    ? "bg-orange-400 hover:bg-orange-500 border-orange-500"
                    : "bg-gray-200 border-gray-300 cursor-not-allowed opacity-50"
                }`}
              >
                <span
                  className={`flex-col-el flex-center type-md ${
                    canTest ? "text-white" : "text-gray-400"
                  }`}
                >
                  <PlayArrowIcon fontSize="inherit" />
                </span>
                <span className={`type-sm ${canTest ? "text-white" : "text-gray-400"}`}>
                  {I18n.t("MSG_BUILDER_TEST_BTN")}
                </span>
              </button>

              {/* 保存ずみ */}
              <div className="flex-row-el flex-center gap-1 whitespace-nowrap">
                <span className="flex-col-el flex-center type-md text-emerald-500">
                  <CloudDoneIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-gray-500">{I18n.t("MSG_BUILDER_SAVED")}</span>
              </div>
            </div>

            {/* 完成ロゴ */}
            <div className="flex-col-el flex-center w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg">
              <span className="type-title">🧩</span>
            </div>

            {/* ？ヘルプ（spec.md §5.8.1） */}
            <button
              onClick={handleOpenTutorial}
              aria-label={I18n.t("MSG_BUILDER_HELP_BTN")}
              className="flex-col-el flex-center w-12 h-12 rounded-full bg-white border-2 border-gray-300 active:scale-95 transition-colors type-inherit-font"
            >
              <span className="flex-col-el flex-center type-title text-orange-400">
                <HelpOutlineIcon fontSize="inherit" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* モーダル */}
      <AIChatModal open={showAIModal} onClose={() => setShowAIModal(false)} />
      <BuilderTutorialOverlay open={showTutorial} onClose={() => setShowTutorial(false)} />
    </>
  );
};
